/**
 * Wraps `IndicatorSelect` with the TASK-115 "heading type switch" UX:
 * when the cataloger picks a different heading type and the current
 * field-instance carries data in subfields that would lose meaning under
 * the new type, a `ConfirmationModal` asks before clearing those fields.
 *
 * Which slots lose meaning under a heading type is not decided here: it is the
 * field's own `visibleWhen` predicates, read by the codec registry
 * (`hiddenHeadingTypeSlots`, CZNA-485) — the same predicates `DynamicFieldPanel`
 * draws with, so a slot is cleared exactly when it is about to disappear.
 *
 * Form-state mutations go through the supplied `getValues` / `setValue`
 * RHF accessors so the change is observed by `MarcPreviewPanel` and the
 * `DynamicFieldPanel` visibility predicates.
 */
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { ConfirmationModal } from '@folio/stripes/components';
import IndicatorSelect from './IndicatorSelect';
import { indicatorSelectId } from '../renderer/domIds';
import { hiddenHeadingTypeSlots, visibilityInd1 } from '../codec/codecRegistry';
/** Does this slot carry anything a cataloger typed or picked? */
var slotHasContent = function (slot) {
    if (!slot)
        return false;
    if ((slot.value || '').trim().length > 0)
        return true;
    if (Object.values(slot.components || {}).some(function (c) { return (c || '').trim().length > 0; }))
        return true;
    // CZNA-310: an imported flat value the cataloger has not edited is emitted
    // by the codec ON ITS OWN, ahead of the components. A slot carrying only
    // that is still a slot carrying data.
    if ((slot.importedValue || '').trim().length > 0)
        return true;
    // CZNA-752: a multi-value subfield keeps its content in `values[]` and leaves
    // `value` empty. Reading only `value` reported "nothing to clear" for a `$c`
    // full of picked terms, so the switch took its no-confirm early return and
    // left them in form state — invisible (the `$c` box is hidden for `ind1='3'`)
    // and still on their way into the stored heading.
    return (slot.values || []).some(function (entry) { return ((entry === null || entry === void 0 ? void 0 : entry.value) || '').trim().length > 0; });
};
var slotKey = function (_a) {
    var code = _a.code, componentType = _a.componentType;
    return (componentType ? "".concat(code, "-").concat(componentType) : code);
};
/** Does the form state hold anything in this slot? */
var slotIsFilled = function (instance, slot) {
    var _a, _b;
    return (slot.componentType
        ? (((_b = (_a = instance[slot.code]) === null || _a === void 0 ? void 0 : _a.components) === null || _b === void 0 ? void 0 : _b[slot.componentType]) || '').trim().length > 0
        : slotHasContent(instance[slot.code]));
};
/**
 * The slots a switch from `oldInd1` to `newInd1` would hide while they hold
 * data — the ones the confirm modal is about. Empty list → no confirm needed.
 * A slot already hidden under `oldInd1` is not listed: the cataloger cannot
 * see it, so there is nothing to warn them about losing.
 */
var detectClearableSlots = function (fieldDef, instance, oldInd1, newInd1) {
    if (!instance)
        return [];
    var hiddenBefore = new Set(hiddenHeadingTypeSlots(fieldDef, oldInd1).map(slotKey));
    return hiddenHeadingTypeSlots(fieldDef, newInd1)
        .filter(function (slot) { return !hiddenBefore.has(slotKey(slot)) && slotIsFilled(instance, slot); })
        .map(slotKey);
};
var NameTypeIndicatorControl = function (_a) {
    var fieldDef = _a.fieldDef, indicatorDef = _a.indicatorDef, value = _a.value, onChange = _a.onChange, pathPrefix = _a.pathPrefix, getValues = _a.getValues, setValue = _a.setValue;
    var intl = useIntl();
    var _b = useState(null), pendingValue = _b[0], setPendingValue = _b[1];
    var handleSelectChange = function (newValue) {
        if (newValue === value)
            return;
        // Read the current instance from form state to decide whether to confirm.
        // Deep paths are not statically resolvable — cast via unknown.
        var getValuesAny = getValues;
        var instance = getValuesAny(pathPrefix);
        // Judged like the renderer judges it: an unset indicator shows the
        // field's default heading type, so that is what the cataloger sees.
        var shownInd1 = visibilityInd1(fieldDef, getValuesAny("".concat(pathPrefix, "._ind1.value")) || undefined);
        var clearable = detectClearableSlots(fieldDef, instance, shownInd1, newValue);
        if (clearable.length === 0) {
            onChange(newValue);
            return;
        }
        setPendingValue(newValue);
    };
    var applyClearAndSwitch = function () {
        if (pendingValue === null)
            return;
        var newInd1 = pendingValue;
        var getValuesAny = getValues;
        var instance = getValuesAny(pathPrefix);
        var clear = function (path, empty) {
            if (empty === void 0) { empty = ''; }
            return (setValue("".concat(pathPrefix, ".").concat(path), empty, { shouldDirty: true }));
        };
        /**
         * A whole subfield: `value` and every component slot. Also
         * `sourceAuthorityId`, so a stale FK from a vocabulary record-picker
         * cannot re-pair with newly typed free text after a round-trip.
         */
        var clearSubfield = function (code) {
            var _a, _b;
            clear("".concat(code, ".value"));
            clear("".concat(code, ".sourceAuthorityId"));
            // CZNA-752: and the multi-value payload, which is where a
            // `multi-vocabulary` subfield ($c) actually keeps its terms. Written
            // only when the slot already carries the key, so a single-value
            // subfield does not grow a `values: []` the codec would then have to
            // ignore.
            if ((_a = instance === null || instance === void 0 ? void 0 : instance[code]) === null || _a === void 0 ? void 0 : _a.values) {
                clear("".concat(code, ".values"), []);
            }
            // And the imported passthrough, for the same reason: it outranks the
            // components on the save path, so clearing them alone would leave the
            // original flat value on its way into the record.
            if (((_b = instance === null || instance === void 0 ? void 0 : instance[code]) === null || _b === void 0 ? void 0 : _b.importedValue) !== undefined) {
                clear("".concat(code, ".importedValue"));
            }
            // `$d`'s BIRTH_DATE / DEATH_DATE, and any other declared component.
            var sf = fieldDef.subfields.find(function (candidate) { return candidate.code === code; });
            for (var _i = 0, _c = Array.from(new Set(((sf === null || sf === void 0 ? void 0 : sf.components) || []).map(function (c) { return c.componentType; }))); _i < _c.length; _i++) {
                var componentType = _c[_i];
                clear("".concat(code, ".components.").concat(componentType));
            }
        };
        for (var _i = 0, _a = hiddenHeadingTypeSlots(fieldDef, newInd1); _i < _a.length; _i++) {
            var slot = _a[_i];
            if (slot.componentType) {
                clear("".concat(slot.code, ".components.").concat(slot.componentType));
            }
            else {
                clearSubfield(slot.code);
            }
        }
        onChange(newInd1);
        setPendingValue(null);
    };
    var dismiss = function () { return setPendingValue(null); };
    return (React.createElement(React.Fragment, null,
        React.createElement(IndicatorSelect, { indicatorDef: indicatorDef, value: value || ' ', onChange: handleSelectChange, 
            // Always ind1: this control exists only for the heading-type switch.
            inputId: indicatorSelectId(pathPrefix, 1) }),
        React.createElement(ConfirmationModal, { open: pendingValue !== null, heading: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.nameTypeSwitch.confirmTitle' }), message: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.nameTypeSwitch.confirmMessage' }), confirmLabel: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.nameTypeSwitch.confirmClear' }), cancelLabel: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.nameTypeSwitch.confirmKeep' }), onConfirm: applyClearAndSwitch, onCancel: dismiss })));
};
export default NameTypeIndicatorControl;
